import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, ListTodoIcon, Loader2Icon, SendIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import { PortalAttachments } from '@/components/attachments/attachment-gallery'
import { PortalAttachmentPicker } from '@/components/attachments/portal-attachment-picker'
import { EmptyState } from '@/components/empty-state'
import { Markdown } from '@/components/markdown'
import { MarkdownEditor } from '@/components/markdown-editor'
import { PageHeader } from '@/components/page-header'
import { RequestTracker } from '@/components/portal/request-tracker'
import { DueDate } from '@/components/tasks/due-date'
import { TaskPriorityIcon } from '@/components/tasks/task-badges'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { usePendingAttachments } from '@/hooks/use-pending-attachments'
import { msg } from '@/i18n'
import { formatRelative } from '@/lib/format'
import { portalName } from '@/lib/portal'
import {
  portalTaskState,
  REQUEST_KIND_LABELS,
  REQUEST_STATE_LABELS,
  REQUEST_STATE_VARIANTS,
} from '@/lib/requests'

export const Route = createFileRoute('/portal/tasks/$taskKey')({
  component: PortalTaskPage,
})

function PortalTaskPage() {
  const { taskKey } = Route.useParams()
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const pieces = usePendingAttachments()
  const t = msg().portalRequests.task

  const task = useQuery({
    queryKey: ['portal', 'task', taskKey],
    queryFn: () => portalApi.task(taskKey),
  })

  const comment = useMutation({
    mutationFn: () =>
      portalApi.addComment(taskKey, {
        body: body.trim(),
        ...(pieces.ids.length > 0 ? { attachment_ids: pieces.ids } : {}),
      }),
    onSuccess: () => {
      setBody('')
      pieces.clear()
      void queryClient.invalidateQueries({ queryKey: ['portal'] })
    },
    onError: (error) => {
      // Une pièce refusée se dit sur la pièce.
      if (pieces.markRefused(error)) return
      toast.error(error instanceof ApiError ? error.message : t.commentFailed)
    },
  })

  if (task.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    )
  }

  if (task.isError) {
    return (
      <EmptyState
        icon={ListTodoIcon}
        title={t.notFoundTitle}
        description={t.notFoundDescription}
        action={
          <Button asChild variant="outline">
            <Link to="/portal/tasks">
              <ArrowLeftIcon />
              {t.backToRequests}
            </Link>
          </Button>
        }
      />
    )
  }

  const data = task.data

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={
          <Button asChild variant="ghost" size="sm">
            <Link to="/portal/tasks">
              <ArrowLeftIcon />
              {t.myRequests}
            </Link>
          </Button>
        }
        title={
          <span className="flex flex-wrap items-center gap-2">
            <code className="font-key rounded bg-band-foreground/10 px-1.5 py-0.5 text-base font-normal">
              {data.key}
            </code>
            <span className="text-balance">{data.title}</span>
          </span>
        }
        documentTitle={`${data.key} · ${data.title}`}
        description={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {/* #331 : l'état lisible par le client, jamais le statut interne. */}
            <Badge
              variant={REQUEST_STATE_VARIANTS[portalTaskState(data)]}
              data-testid="etat-demande"
            >
              {REQUEST_STATE_LABELS[portalTaskState(data)]}
            </Badge>
            {data.request_kind && <span>{REQUEST_KIND_LABELS[data.request_kind]}</span>}
            {data.project && <span>{t.project(data.project.name)}</span>}
            <TaskPriorityIcon priority={data.priority} withLabel />
            {data.due_date && (
              <span className="flex items-center gap-1">
                {t.due} <DueDate dueDate={data.due_date} status={data.status} />
              </span>
            )}
            {data.assignee_name && <span>{t.followedBy(data.assignee_name)}</span>}
          </span>
        }
      />

      {data.request_state && (
        <Card>
          <CardHeader>
            <CardTitle>{t.tracking}</CardTitle>
          </CardHeader>
          <CardContent>
            <RequestTracker state={data.request_state} />
          </CardContent>
        </Card>
      )}

      {(data.description !== '' || data.request_url !== null || data.attachments.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>{t.description}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.description && <Markdown>{data.description}</Markdown>}
            {data.request_url && (
              <p className="text-sm" data-testid="demande-page">
                {t.page}{' '}
                <a
                  href={data.request_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="break-all text-primary underline-offset-2 hover:underline"
                >
                  {data.request_url}
                </a>
              </p>
            )}
            <PortalAttachments attachments={data.attachments} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t.exchanges}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.comments.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.noExchange}</p>
          ) : (
            <ul className="space-y-4">
              {data.comments.map((item) => (
                <li key={item.id} className="space-y-1" data-testid="portal-comment">
                  <p className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium">{portalName(item.author_name)}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatRelative(item.created_at)}
                    </span>
                  </p>
                  <Markdown>{item.body}</Markdown>
                  <PortalAttachments attachments={item.attachments} />
                </li>
              ))}
            </ul>
          )}

          <form
            className="space-y-2 border-t pt-4"
            onSubmit={(event) => {
              event.preventDefault()
              if (body.trim() && !pieces.busy && !pieces.blocked) comment.mutate()
            }}
          >
            <div onPaste={pieces.onPaste}>
              <MarkdownEditor
                value={body}
                onChange={setBody}
                rows={3}
                placeholder={t.replyPlaceholder}
              />
            </div>
            <PortalAttachmentPicker pending={pieces} />
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!body.trim() || comment.isPending || pieces.busy || pieces.blocked}
              >
                {comment.isPending ? <Loader2Icon className="animate-spin" /> : <SendIcon />}
                {t.send}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
